import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSubjectList(
    $filter: subjectFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSubjectList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        keyValueList {
          value
          organizationSubjectKey {
            name
            priorityOrder
          }
        }
        isArchived
        organizationId
        mobile
        email
        photo
        bloodGroup
        dob
        gender
        name
        groupId
        subjectType
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.SubjectListState = {
  result: null,
  error: null,
  loading: false
};

export const getSubjectListRequest = createAsyncThunk<
  TYPES.SubjectList,
  {
    filter?: TYPES.SubjectFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'subject/get_subject_list',
  async (
  params: {
    filter?: TYPES.SubjectFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSubjectList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSubjectListSlice = createSlice({
  name: 'get_subject_list',
  initialState: initialState,
  reducers: {
    getSubjectListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSubjectListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getSubjectListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getSubjectListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSubjectListClear } = getSubjectListSlice.actions;

export const getSubjectList = createSelector(
  (state: RootState) => state.subject.getSubjectList.result,
  (state: RootState) => state.subject.getSubjectList.error,
  (state: RootState) => state.subject.getSubjectList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSubjectListSlice.reducer;